/**
 * storageManager.ts
 *
 * 목적: localStorage 버전 관리 + 안전 파싱 레이어
 *
 * - 업그레이드 배포 후 localStorage 구조 불일치로 인한 설정 초기화 방지
 * - STORAGE_SCHEMA_VERSION을 올리면 migrate() 함수에서 구버전 데이터를 안전하게 변환
 * - safeGet/safeSet: JSON.parse 실패 시 초기화 대신 fallback 값을 반환
 * - clearSensitive: 로그아웃 시 민감 정보 클리어
 */

/** localStorage 스키마 버전. 저장 구조 변경 시 증가. */
const STORAGE_SCHEMA_VERSION = 1;
const VERSION_KEY = 'mmo_storage_schema_version';

export const StorageManager = {
  /**
   * 앱 초기화 시 호출 (로그인 완료 후).
   * 저장된 버전이 현재 버전보다 낮으면 마이그레이션 실행.
   */
  init(userId: string): void {
    const savedVersion = parseInt(
      localStorage.getItem(VERSION_KEY) ?? '0',
      10
    );
    if (savedVersion < STORAGE_SCHEMA_VERSION) {
      this._migrate(savedVersion, STORAGE_SCHEMA_VERSION, userId);
      localStorage.setItem(VERSION_KEY, String(STORAGE_SCHEMA_VERSION));
      console.info(
        `[StorageManager] 스키마 v${savedVersion} → v${STORAGE_SCHEMA_VERSION} 마이그레이션 완료`
      );
    }
  },

  /**
   * 버전별 마이그레이션 로직.
   * 새 버전 추가 시 이 함수에 if (from < N) 블록 추가.
   */
  _migrate(from: number, to: number, userId: string): void {
    // v0 → v1: 구 게스트 키 네이밍 정규화
    //   'macroOptix_apiKeys' → 'mmo_{uid}_apiKeys'
    if (from < 1) {
      const oldGuestKey = localStorage.getItem('macroOptix_apiKeys');
      if (oldGuestKey && !localStorage.getItem(`mmo_${userId}_apiKeys`)) {
        localStorage.setItem(`mmo_${userId}_apiKeys`, oldGuestKey);
        localStorage.removeItem('macroOptix_apiKeys');
        console.info('[StorageManager] v0→v1: 게스트 API 키 이전 완료');
      }
    }
    // v1 → v2: 향후 구조 변경 시 여기에 추가
    // if (from < 2) { ... }
  },

  /**
   * localStorage에서 안전하게 값을 읽는다.
   * JSON.parse 실패 시 fallback 값을 반환하고 손상된 데이터를 제거.
   */
  safeGet<T>(key: string, fallback: T): T {
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) return fallback;
      return JSON.parse(raw) as T;
    } catch {
      console.warn(
        `[StorageManager] "${key}" 파싱 실패 — 손상된 데이터 제거 후 기본값 사용`
      );
      localStorage.removeItem(key);
      return fallback;
    }
  },

  /**
   * localStorage에 안전하게 값을 저장한다.
   * QuotaExceededError 등의 예외를 catch하여 앱 크래시 방지.
   */
  safeSet(key: string, value: unknown): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.error(`[StorageManager] "${key}" 저장 실패`, e);
    }
  },

  /**
   * localStorage에서 특정 키를 안전하게 제거.
   */
  safeRemove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn(`[StorageManager] "${key}" 삭제 실패`, e);
    }
  },

  /**
   * 로그아웃 시 민감 정보 및 사용자 프로필 캐시를 localStorage에서 제거.
   * 캐시성 데이터(indicators, xray 등)는 유지하여 불필요한 재로드 방지.
   */
  clearSensitive(userId: string): void {
    const sensitiveKeys = [
      `mmo_${userId}_apiKeys`,
      // [레거시 정리] 과거 localStorage 평문 자격증명 키 — 단일 저장소(user_api_keys)로 이관 완료되어 잔존 시 제거합니다.
      `mmo_${userId}_kis_credentials`,
      `mmo_${userId}_ls_credentials`,
      `mmo_${userId}_kiwoom_credentials`,
      // [현행] 비밀정보가 없는 표시용 메타 캐시
      `mmo_${userId}_kis_meta`,
      `mmo_${userId}_ls_meta`,
      `mmo_${userId}_kiwoom_meta`,
      `mmo_${userId}_profile_cache`,
      `mmo_${userId}_chat_history`,
    ];
    sensitiveKeys.forEach((key) => localStorage.removeItem(key));
    console.info('[StorageManager] 민감 정보, 프로필 캐시 및 채팅 기록 localStorage 클리어 완료');
  },
};
